import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import BASE_URL from "../../assets/global/url";

/**
 * Custom hook for Payable ReOpen Ticket ID Generator
 * Generate a code then change prefix of a transaction ID from 'PO-' to 'ROT-'
 * @param {boolean} skip - If true, skips fetching the code from the server.
 * @returns {string} The formatted ID (e.g., "ROT-20260214023986")
 */

const useReOpenTicketIdGenerator = (skip = false) => {
  const [codeGenerated, setCodeGenerated] = useState("");

  // Fetch Code Generator Data
  useEffect(() => {
    // If we want to skip (e.g., in view mode), exit the effect early
    if (skip) return;

    let isMounted = true; // Cleanup flag to prevent state updates on unmounted components

    const fetchCode = async () => {
      try {
        const res = await axios.get(BASE_URL + "/payable/getCode");
        if (isMounted) {
          setCodeGenerated(res.data);
        }
      } catch (error) {
        console.error("Failed to fetch ReOpen Ticket ID:", error);
      }
    };

    fetchCode();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [skip]);

  const formattedTransactionId = useMemo(() => {
    // Return empty string if id is undefined or null
    if (!codeGenerated) return "";

    // Split the id. Example: 'PO-20260214023986' into ['PO', '20260214023986']
    const parts = codeGenerated.split("-");

    // Check if the prefix is "PO" or "SI" and replace it with "ROT"
    if (parts.length > 1 && (parts[0] === "PO" || parts[0] === "SI")) {
      return `ROT-PO-${parts[1]}`;
    }

    // Fallback just in case it's formatted differently
    return codeGenerated.replace("PO-", "ROT-PO-");
  }, [codeGenerated]);

  return formattedTransactionId;
};

export default useReOpenTicketIdGenerator;
